import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommonProperties } from '../common/common.interfaces';
import { useLoading } from '../loading/LoadingContext';
import { useUserContext } from '../user/UserContext';
import CustomError, { CustomErrorProps } from '../common/components/CustomError';
import { Badge, Button, Form } from 'react-bootstrap';
import { apiFetch } from '../utility/fetch';
import { API_DEV_URL, API_PROD_URL } from '..';
import { Shipping_Detail__c, Shipping_Detail__c_Update } from './shipDetailReset.interfaces';
import { SalesforceQueryResponse } from '../orders/orders.interfaces';
import { notify } from '../notify/notify';
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../common/common.entities';
import { DateTime } from 'luxon';
import ShipDetailResetConfirm from './ShipDetailResetConfirm';
import { SalesforceUpdateRequestBody } from '../common/common.api.interfaces';

interface ShipDetailResetProps extends CommonProperties{}

export default function ShipDetailReset(props: ShipDetailResetProps){
  // #region STATE
  const {userData} = useUserContext()
  const {toggleLoading, changeStep, changeSubStep} = useLoading()

  const [environment, setEnvironment]: [string, any] = useState('production')
  const [error, setError]: [CustomErrorProps, any] = useState({visible: false})
  const [searchText, setSearchText]: [string, any] = useState('')
  const [shipDetail, setShipDetail]: [Shipping_Detail__c, any] = useState(undefined)
  const [resetShipDetail, setResetShipDetail]: [Shipping_Detail__c_Update, any] = useState(undefined)
  const [showConfirm, setShowConfirm]: [boolean, any] = useState(false)
  const [fulfilledBySoftwares, setFulfilledBySoftwares] = useState([])

  // Allows ?id=order-number in the url to load an order via URL
  // #region LOADING
  useEffect(() => {
    if(window.location.search){
      const urlParams = new URLSearchParams(window.location.search);
      Promise.resolve(load(urlParams.get('id')))
    }
  }, [environment])

  useEffect(() => {
    setResetShipDetail({Id: shipDetail?.Id})
  }, [shipDetail])

  const load = async function(orderNumber: string) {
    setError({visible: false})

    changeStep('Loading Ship Detail')
    changeSubStep(`Finding Ship Detail containing '${orderNumber}'`)
    toggleLoading(true)
    const response = await apiFetch<SalesforceQueryResponse<Shipping_Detail__c>>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'development' ? API_DEV_URL : API_PROD_URL,
      body: JSON.stringify({
        soql: `SELECT Id, Order__r.Account.Hours_to_Invoice__c, Name, Shipment_Sent_Time__c, Shipment_Time__c, Shipment_Uploaded_Time__c, Invoice_Uploaded_Time__c, Tracking_Number__c, Shipping_Carrier__c, Shipping_Class__c, Fulfilled_by_Software__c, ShipStation_Order_Id__c, Fulfilled_by_Software_URL__c, BatchName__c, Shipping_Status__c, Order__r.Id, Order__r.Name FROM Shipping_Detail__c WHERE Order__r.Name = '${orderNumber}'`
      })
    })
    if(!response.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(response?.body),
        description: `Failed to get Ship Detail. Response not OK. Status: ${response.status}`,
        title: `Ship Detail Query Error`,
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
    }
    if(!response.body?.records?.length || response.body.records.length === 0){
      notify('warning', `Could not find a ship detail using '${orderNumber}'!`)
      toggleLoading(false)
    }
    const softwareResponse = await apiFetch<SalesforceQueryResponse<{Label: string, Value: string}>>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'development' ? API_DEV_URL : API_PROD_URL,
      body: JSON.stringify({
        soql: `SELECT Label, Value FROM PicklistValueInfo WHERE EntityParticle.EntityDefinition.QualifiedApiName = 'Shipping_Detail__c' AND EntityParticle.QualifiedApiName = 'Fulfilled_by_Software__c' AND IsActive = true`
      })
    })
    setFulfilledBySoftwares(softwareResponse.body.records)
    setShipDetail(response.body.records[0])
    toggleLoading(false)
  }

  // #region CALLBACKS
  const loadClicked = useCallback(async () => {
    if(!searchText) return
    await load(searchText)
  }, [searchText, environment])

  const handlePendingTrackingClicked = useCallback((e) => {
    resetShipDetail.Shipment_Uploaded_Time__c = null
    resetShipDetail.Azure_Status__c = null
    resetShipDetail.Shipping_Status_Calc__c = 'Shipped'
    setResetShipDetail({...resetShipDetail})
    setShowConfirm(true)
  }, [resetShipDetail])

  const handlePendingInvoiceClicked = useCallback((e) => {
    resetShipDetail.Invoice_Uploaded_Time__c = null
    resetShipDetail.Azure_Status__c = null
    resetShipDetail.Shipping_Status_Calc__c = 'Pending Invoice'
    setResetShipDetail({...resetShipDetail})
    setShowConfirm(true)
  }, [resetShipDetail])

  const handlePendingSoftwareClicked = useCallback((e) => {
    resetShipDetail.Shipment_Uploaded_Time__c = null
    resetShipDetail.Azure_Status__c = null
    resetShipDetail.Shipping_Status_Calc__c = 'Pending'
    resetShipDetail.Fulfilled_by_Software__c = null
    resetShipDetail.BatchName__c = null
    resetShipDetail.Shipment_Time__c = null
    resetShipDetail.ShipStation_Order_Id__c = null
    resetShipDetail.Tracking_Number__c = null
    resetShipDetail.Shipment_Sent_Time__c = null
    setResetShipDetail({...resetShipDetail})
    setShowConfirm(true)
  }, [resetShipDetail])

  const handleConfirmClicked = useCallback(async (e) => {
    setError({visible: false})
    changeStep('Updating Ship Detail')
    changeSubStep(`Updating Ship Detail '${resetShipDetail.Id}' in Mission Control`)
    toggleLoading(true)

    const response = await apiFetch({
      endpoint: 'salesforceUpdate',
      method: 'PATCH',
      accessToken: userData.accessToken,
      baseUrlOverride: environment === 'development' ? API_DEV_URL : API_PROD_URL,
      body: JSON.stringify({
        idOrExternalIdField: 'Id',
        items: [resetShipDetail],
        sObject: 'Shipping_Detail__c'
      } as SalesforceUpdateRequestBody)
    })
    if(!response.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(response?.body),
        description: `Failed to update Ship Detail. Response not OK. Status: ${response.status}`,
        title: `Ship Detail Update Error`,
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      setShowConfirm(false)
      return
    }
    setShowConfirm(false)
    setResetShipDetail(undefined)
    notify('success', `Ship Detail successfully updated!`)
    toggleLoading(false)
    await load(shipDetail?.Order__r?.Name)
  }, [resetShipDetail, environment])

  const handleFulfillmentSoftwareChange = useCallback((e) => {
    if(e.target.value === '-- SELECT A FULFILLMENT SOFTWARE --'){
      resetShipDetail.Fulfilled_by_Software__c = null
    } else {
      resetShipDetail.Fulfilled_by_Software__c = e.target.value
    }
    setResetShipDetail({...resetShipDetail})
  }, [resetShipDetail])

  // #region VIEW
  const view = useMemo(() => {
    return <div>
    <ShipDetailResetConfirm handleConfirm={handleConfirmClicked} closeModal={() => {setShowConfirm(false)}} modalVisible={showConfirm} shipDetail={resetShipDetail} title={`Confirm Ship Detail Reset for Order ${shipDetail?.Order__r?.Name}`} />
    <div id='top'></div>
    <div className='beacon-card'>
      <table className='beacon-table'>
        <tbody>
          <tr>
            <td><span className='beacon-header'>Ship Detail Reset</span></td>
          </tr>
          <tr>
            <td><span className='beacon-label'>Reset a ship detail to be pending software, pending tracking, or pending invoice.</span></td>
          </tr>
          {userData.roles.includes('admin') &&
            <tr>
              <td>
                <Button onClick={() => {setEnvironment('development')}} variant={environment === 'development' ? 'secondary' : 'outline-secondary'}>SANDBOX</Button>&nbsp;
                <Button onClick={() => {setEnvironment('production')}} variant={environment === 'development' ? 'outline-secondary' : 'secondary'}>PRODUCTION</Button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
    <br/>
    <CustomError 
      closeError={() => {setError({visible: false})}}
      content={error?.content}
      description={error?.description}
      title={error?.title}
      visible={error?.visible}
    />
    <div className='beacon-card'>
      <span className='beacon-label'>Enter an Order Number to load in a Ship Detail to reset.</span>
      <Form.Control
        type='text'
        placeholder='Order Number...'
        style={{width: '350px'}}
        onChange={(e) => setSearchText(e.target.value)}
      />
      <Button variant='secondary' onClick={loadClicked}>LOAD</Button>
    </div>
    <br/>
    {shipDetail && <div className="beacon-card">
      <span className='beacon-header'>
        <a title='Open Order in Mission Control' className='beacon-link' target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT[environment]}/${shipDetail?.Order__r?.Id}`}>
          {shipDetail?.Order__r?.Name}
        </a>&nbsp;-&nbsp; 
        <a title='Open Shipping Detail in Mission Control' className='beacon-link' target="_blank" rel="noopener noreferrer" href={`${SALESFORCE_URLS_BY_ENVIRONMENT[environment]}/${shipDetail?.Id}`}>
          {shipDetail?.Name}
        </a>
      </span>
      <br/><Badge bg='secondary' style={{fontSize: 'larger'}}>{shipDetail?.Shipping_Status__c?.toUpperCase()}</Badge>
      <div style={{fontSize: 'larger', display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
        <div style={{width: '45%'}}>
          <span className='beacon-label'>TRACKING NUMBER: </span> <span className='beacon-label'>{shipDetail?.Tracking_Number__c}</span><br/>
          {shipDetail?.Tracking_Number__c &&
            <>
              <a className='beacon-link' title={'View the tracking information on the FedEx site.'} target="_blank" rel="noopener noreferrer" href={`https://www.fedex.com/wtrk/track/?tracknumbers=${shipDetail?.Tracking_Number__c}`}>
                <Button variant='outline-secondary'>FEDEX</Button>
              </a>
              &nbsp;<a className='beacon-link' title={'View the tracking information on the UPS site.'} target="_blank" rel="noopener noreferrer" href={`www.ups.com/track?track=yes&trackNums=${shipDetail?.Tracking_Number__c}`}>
                <Button variant='outline-secondary'>UPS</Button>
              </a>
              &nbsp;<a className='beacon-link' title={'View the tracking information on the USPS site.'} target="_blank" rel="noopener noreferrer" href={`https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${shipDetail?.Tracking_Number__c}`}>
                <Button variant='outline-secondary'>USPS</Button>
              </a>
              <br/>
            </>
          }
          <span className='beacon-label'>CARRIER: </span> <span className='beacon-label'>{shipDetail?.Shipping_Carrier__c}</span><br/>
          <span className='beacon-label'>CARRIER CLASS: </span> <span className='beacon-label'>{shipDetail?.Shipping_Class__c}</span>
          {shipDetail?.Fulfilled_by_Software_URL__c && <><br/><a title='Open Order in Fulfillment Software' className='beacon-link' target="_blank" rel="noopener noreferrer" href={shipDetail?.Fulfilled_by_Software_URL__c}><Button variant='outline-secondary'>{shipDetail?.Fulfilled_by_Software__c?.toUpperCase()}</Button></a><br/></>}

          
        </div>
        <div style={{width: '45%'}}>
          <span className='beacon-label'>LAST SOFTWARE CREATE: </span> <span className='beacon-label'>{shipDetail?.Shipment_Sent_Time__c && DateTime.fromISO(shipDetail?.Shipment_Sent_Time__c).toFormat('MM/dd/yyyy t')}</span><br/>
          <span className='beacon-label'>LAST LABEL CREATE: </span> <span className='beacon-label'>{shipDetail?.Shipment_Time__c && DateTime.fromISO(shipDetail?.Shipment_Time__c).toFormat('MM/dd/yyyy t')}</span><br/>
          <span className='beacon-label'>LAST TRACKING SENT: </span> <span className='beacon-label'>{shipDetail?.Shipment_Uploaded_Time__c && DateTime.fromISO(shipDetail?.Shipment_Uploaded_Time__c).toFormat('MM/dd/yyyy t')}</span><br/>
          <span className='beacon-label'>LAST INVOICE SENT: </span> <span className='beacon-label'>{shipDetail?.Invoice_Uploaded_Time__c && DateTime.fromISO(shipDetail?.Invoice_Uploaded_Time__c).toFormat('MM/dd/yyyy t')}</span><br/>
        </div>
      </div>
      <hr/>
      <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
        <div className='beacon-card' style={{width: '33%'}}>
          <span className='beacon-header'>RESET TO PENDING SOFTWARE</span>
          <br/>
          <span className='beacon-label'>This will clear out any existing tracking on the ship detail and reset it back to where it will determine the tracking software.</span>
          <br/>
          <Button variant='secondary' onClick={handlePendingSoftwareClicked}>RESET TO PENDING SOFTWARE</Button>
          <hr/>
          All tracking fields will be cleared.<br/>
          Shipping Status Calc will be set to Pending.
        </div>
        <div className='beacon-card' style={{width: '33%'}}>
          <span className='beacon-header'>RESET TO PENDING TRACKING "SHIPPED"</span>
          <br/>
          <span className='beacon-label'>This will clear out any fields that are required to set it back to "SHIPPED" so that it can have its tracking information sent to the marketplace again.</span>
          <br/><br/>
          <span className='beacon-label'>OPTIONAL: Modify tracking details.</span><br/>
        
          <span>Tracking Number</span>
          <Form.Control
            type='text'
            value={resetShipDetail?.Tracking_Number__c ?? shipDetail?.Tracking_Number__c ?? ''}
            placeholder='Tracking number...'
            onChange={(e) => setResetShipDetail({...resetShipDetail, Tracking_Number__c: e.target.value})}
          />

          <span>Fulfillment Software</span>
          <Form.Select onChange={handleFulfillmentSoftwareChange} value={shipDetail?.Fulfilled_by_Software__c ?? ''} style={{width: '200px'}}>
            <option>-- SELECT A FULFILLMENT SOFTWARE --</option>
            {fulfilledBySoftwares?.sort()?.map(sw => (
              <option key={sw.Value} value={sw.Value}>{sw.Label}</option>
            ))}
          </Form.Select>

          <span>Fulfillment Software ID</span>
          <Form.Control
            type='text'
            value={resetShipDetail?.ShipStation_Order_Id__c ?? shipDetail?.ShipStation_Order_Id__c ?? ''}
            placeholder='Fulfillment Software ID...'
            onChange={(e) => setResetShipDetail({...resetShipDetail, ShipStation_Order_Id__c: e.target.value})}
          />

          <span>Shipping Carrier</span>
          <Form.Control
            type='text'
            value={resetShipDetail?.Shipping_Carrier__c ?? shipDetail?.Shipping_Carrier__c ?? ''}
            placeholder='Shipping Carrier...'
            onChange={(e) => setResetShipDetail({...resetShipDetail, Shipping_Carrier__c: e.target.value})}
          />

          <span>Shipping Class</span>
          <Form.Control
            type='text'
            value={resetShipDetail?.Shipping_Class__c ?? shipDetail?.Shipping_Class__c ?? ''}
            placeholder='Shipping Class...'
            onChange={(e) => setResetShipDetail({...resetShipDetail, Shipping_Class__c: e.target.value})}
          />
          <br/>
          <Button onClick={handlePendingTrackingClicked} variant='secondary'>RESET TO PENDING TRACKING</Button>
          <hr/>
          Azure Status will be cleared. <br/>
          Shipment Uploaded Time will be cleared.<br/>
          Shipping Status Calc will be set to Shipped.<br/>
          {resetShipDetail?.Tracking_Number__c && <>Tracking Number will be changed to {resetShipDetail?.Tracking_Number__c}.<br/></>}
          {resetShipDetail?.Shipping_Carrier__c && <>Shipping Carrier will be changed to {resetShipDetail?.Shipping_Carrier__c}.<br/></>}
          {resetShipDetail?.Shipping_Class__c && <>Shipping Class will be changed to {resetShipDetail?.Shipping_Class__c}.<br/></>}
          {resetShipDetail?.ShipStation_Order_Id__c && <>Fulfillment Software ID will be changed to {resetShipDetail?.ShipStation_Order_Id__c}.<br/></>}
          {resetShipDetail?.Fulfilled_by_Software__c && <>Fulfilled by Software will be changed to {resetShipDetail?.Fulfilled_by_Software__c}.<br/></>}
        </div>
        {shipDetail?.Order__r?.Account?.Hours_to_Invoice__c &&
          <div className='beacon-card' style={{width: '33%'}}>
            <span className='beacon-header'>RESET TO PENDING INVOICE</span>
            <br/>
            <span className='beacon-label'>This will clear out any fields that are required to set it back to "PENDING INVOICE" so that it can have its invoice information sent to the marketplace again.</span>
            <br/>
            <Button variant='secondary' onClick={handlePendingInvoiceClicked}>RESET TO PENDING INVOICE</Button>
            <hr/>
            Azure Status will be cleared.<br/>
            Invoice Uploaded Time will be cleared.<br/>
            Shipping Status Calc will be cleared.
          </div>
        }
        {!shipDetail?.Order__r?.Account?.Hours_to_Invoice__c &&
          <div className='beacon-card' style={{width: '33%'}}>
            <span className='beacon-header'>RESET TO PENDING INVOICE</span>
            <br/>
            <span className='beacon-label'>ORDER ACCOUNT DOES NOT SUPPORT INVOICING</span>
          </div>
        }
      </div>
    </div>}
  </div>
  }, [environment, shipDetail, searchText, resetShipDetail, showConfirm])

  return view
}